<template>
	<div class="form-inputs">
		<FormLabelTooltip v-if="label" :name="`select-${name}`" :label="label" />
		<select
			class="b360-form-input b360-form-input--select"
			@change="
				(event) => {
					$emit('change', {
						fieldName: name,
						value: event.target.value,
					});
					disablePlaceholderOption();
				}
			"
			:id="`select-${name}`"
			data-test="form-dropdown-select"
			:required="required"
			:value="value"
			:disabled="disabled"
			v-model="inputVal"
			:class="{
				disabled: disabled,
				error: error,
				placeholder: isPlaceholderValue,
			}"
		>
			<option
				class="placeholder"
				:disabled="placeholderDisabled"
				:value="placeholderValue"
			>
				Select an option...
			</option>
			<option v-for="option in options" :value="option.value" :key="option.id">
				{{ option.name }}
			</option>
		</select>
	</div>
</template>

<script>
/**
* PROPS:
* 	disabled:
* 	If true, disables select and applies the 'disabled' CCS class.
*
* 	error:
* 	If true applies 'error' CSS class to select.
*
* 	label:
* 	Label text for the select form element.
*
* 	name:
* 	Used to create an HTML id for the select. The HTML id will have "select-" prepended to it.
* 	Also used as the fieldName value that is passed on a 'change' event.
*
* 	options:
* 	The array of option objects. Each option object should have the following properties:
* 		{
* 			id: Used as a key for the v-for which generates the options list, must be unique.
* 			value: The value of the select when the option is selected
* 			name: The option label that the user sees
* 		}
*
* 	placeholder:
* 	If true, the 'placeholder' CSS class is applied to select element when value is equal to the placeholderValue.
*
* 	placeholderDisable:
* 	Default is true. If true, the placeholder option will be disabled after an option is selected.
*
* 	placeholderValue:
* 	Default is null. The value when the placeholder is selected.
*
* 	required:
* 	If true, the select is a required field.
*
* 	value:
* 	The selected value of the drop down.  Bound to FormDropdowns using v-model.
* 	This value is emited on change and input events.
*
* EVENTS:
* 	change:
* 	Emitted when the select's value changes.  Returns the following object:
* 		{
* 			fieldName: the value of the 'name' prop
* 			value: the select's value
* 		}
*
* 	input:
* 	Emitted after the select's value is changed, only returns the select's value.
*/

import { isEqual } from 'lodash';
import FormLabelTooltip from '@/components/global/FormLabelTooltip.vue';

export default {
	name: 'FormDropdowns',
	components: {
		FormLabelTooltip,
	},
	props: {
		name: String,
		disabled: Boolean,
		error: Boolean,
		label: String,
		options: Array,
		placeholder: Boolean,
		placeholderDisable: {
			type: Boolean,
			default: true,
		},
		placeholderValue: {
			type: [Object, Array, String, Number, Boolean],
			default: null,
		},
		required: Boolean,
		value: [Object, Array, String, Number, Boolean],
	},
	data() {
		return {
			inputVal: this.value,
			placeholderDisabled: false,
		};
	},
	computed: {
		isPlaceholderValue() {
			return isEqual(this.value, this.placeholderValue);
		},
	},
	watch: {
		inputVal(val) {
			this.$emit('input', val);
		},
		value(val) {
			this.inputVal = val;
		},
	},
	methods: {
		disablePlaceholderOption() {
			this.placeholderDisabled = this.placeholderDisable;
		},
	},
	mounted() {
		if (this.value !== this.placeholderValue && this.placeholderDisable) {
			this.placeholderDisabled = true;
		}
	},
};
</script>

<style lang="scss" scoped>
select.b360-form-input {
	height: 44px;
	padding: 0.6rem 0.75rem;

	&.placeholder {
		color: $placeholder-text-color;
	}

	option {
		color: $subheader-color;
		&.placeholder {
			color: $placeholder-text-color;
		}
	}
}
.error {
	border: 1px solid $error;
}
</style>
